import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AlertCircle, Save, Info, GitBranch, ArrowLeft } from "lucide-react";
import { ProjectService } from "../services/projectService";
import { Modal, ModalBody, ModalFooter, Input, Button, Dropdown } from "./ui";
import { TemplateCategoryService } from "../services/templateCategoryService";
import BaseProjectSelect from "./BaseProjectSelect";

const NewProjectModal = ({
  isOpen,
  onClose,
  onSave,
  clients,
  contacts,
}) => {
  // Step state: 'choose_type' | 'new_project' | 'revision'
  const [step, setStep] = useState('choose_type');

  // Common form data
  const [formData, setFormData] = useState({
    projectName: "",
    clientId: "",
    contactId: "",
    approximateAcreage: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Category-based state (for new projects only)
  const [categories, setCategories] = useState([]);
  const [deliverableType, setDeliverableType] = useState('surface');
  const [projectTypeId, setProjectTypeId] = useState(null);
  const [addOns, setAddOns] = useState({
    hasPond: false,
    elevatedUtilities: false,
    hasErosionSurface: false,
    advancedUtilityModel: false,
    hasCenterlineProfiles: true, // Default true for Subdivision
  });

  // Revision state
  const [selectedBaseProjectId, setSelectedBaseProjectId] = useState(null);
  const [selectedBaseProject, setSelectedBaseProject] = useState(null);

  // Load categories from database
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await TemplateCategoryService.getCategories();
        setCategories(cats);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  // Reset everything when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('choose_type');
      setFormData({
        projectName: "",
        clientId: "",
        contactId: "",
        approximateAcreage: "",
      });
      setDeliverableType('surface');
      setProjectTypeId(null);
      setAddOns({
        hasPond: false,
        elevatedUtilities: false,
        hasErosionSurface: false,
        advancedUtilityModel: false,
        hasCenterlineProfiles: true, // Default true for Subdivision
      });
      setSelectedBaseProjectId(null);
      setSelectedBaseProject(null);
      setError(null);
    }
  }, [isOpen]);

  // Group categories by type
  const categoriesGrouped = useMemo(() => {
    const grouped = {
      base: [],
      deliverable: [],
      project_type: [],
      add_on: [],
      file_format: [],
      unit_system: [],
    };
    categories.forEach(cat => {
      if (grouped[cat.categoryType]) {
        grouped[cat.categoryType].push(cat);
      }
    });
    return grouped;
  }, [categories]);

  // Project type dropdown options
  const projectTypeOptions = useMemo(() => {
    return categoriesGrouped.project_type
      .filter(cat => cat.name !== 'Linework Only' && cat.name !== 'Erosion Surface')
      .map(cat => ({
        value: cat.id,
        // Display "Pond Only" instead of "Pond" for clarity
        label: cat.name === "Pond" ? "Pond Only" : (cat.displayName || cat.name),
      }));
  }, [categoriesGrouped.project_type]);

  // Filter contacts based on selected client
  const filteredContacts = useMemo(() => {
    if (!formData.clientId || !contacts) return [];
    return contacts.filter((contact) => {
      return (
        String(contact.clientId) === String(formData.clientId) ||
        String(contact.client_id) === String(formData.clientId)
      );
    });
  }, [formData.clientId, contacts]);

  // Dropdown options
  const clientOptions = useMemo(() => {
    if (!clients) return [];
    return clients.map((client) => ({
      value: client.id,
      label: client.name,
    }));
  }, [clients]);

  const contactOptions = useMemo(() => {
    if (!filteredContacts) return [];
    return filteredContacts.map((contact) => ({
      value: contact.id,
      label: contact.name,
    }));
  }, [filteredContacts]);

  // Build template filter array from category selections
  const buildTemplateFilterFromCategories = useCallback(() => {
    const filters = [];

    const allProjectsCat = categoriesGrouped.base?.find(c => c.name === 'All Projects');
    if (allProjectsCat) {
      filters.push(`00-${allProjectsCat.name}`);
    }

    if (deliverableType === 'linework') {
      filters.push('01-Linework Design');
    } else {
      filters.push('01-Surface Design');
      if (projectTypeId) {
        const projectTypeCat = categories.find(c => c.id === projectTypeId);
        if (projectTypeCat) {
          filters.push(`02-${projectTypeCat.name}`);
        }
      }
    }

    if (addOns.hasPond) filters.push('02-Pond');
    if (addOns.elevatedUtilities) filters.push('02-Elevated Utilities');
    if (addOns.hasErosionSurface) filters.push('02-Erosion Surface');
    if (addOns.advancedUtilityModel) filters.push('01-Advanced Utility Model');

    return filters;
  }, [categoriesGrouped, deliverableType, projectTypeId, categories, addOns]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "clientId") {
        newData.contactId = "";
        // Clear base project if client changes in revision mode
        if (step === 'revision') {
          setSelectedBaseProjectId(null);
          setSelectedBaseProject(null);
        }
      }
      return newData;
    });
  };

  // Handle base project selection
  const handleBaseProjectSelect = (e) => {
    const projectId = e.target.value ? (typeof e.target.value === 'number' ? e.target.value : parseInt(e.target.value, 10)) : null;
    const baseProject = e.project;

    setSelectedBaseProjectId(projectId);
    setSelectedBaseProject(baseProject);

    if (baseProject) {
      // Auto-fill project name with base project name + "Revision"
      const defaultName = `${baseProject.name} Revision`;
      setFormData(prev => ({
        ...prev,
        projectName: defaultName,
        // Auto-fill client from base project
        clientId: baseProject.clientId || baseProject.client_id || prev.clientId,
        // Auto-fill contact from base project
        contactId: baseProject.contactId || baseProject.contact_id || prev.contactId
      }));
    }
  };

  // Handle add-on toggle
  const handleAddOnToggle = (key) => {
    setAddOns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.projectName) {
      setError("Project name is required");
      return;
    }

    if (step === 'revision') {
      if (!selectedBaseProjectId) {
        setError("Please select a base project");
        return;
      }
    } else {
      if (!formData.clientId) {
        setError("Client is required");
        return;
      }
      if (deliverableType === 'surface' && !projectTypeId) {
        setError("Please select a project type");
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const createData = {
        name: formData.projectName,
        modeling_status: "Startup",
        modeling_state: "Active",
      };

      if (step === 'revision' && selectedBaseProject) {
        // Revision: minimal data, inherit from base project
        createData.job_number = selectedBaseProject.jobNumber;
        createData.rev = null; // Let trigger calculate
        createData.parent_project_id = selectedBaseProjectId;
        createData.is_revision = true;
        createData.client_id = selectedBaseProject.clientId || selectedBaseProject.client_id || null;
        createData.contact_id = selectedBaseProject.contactId || selectedBaseProject.contact_id || null;
      } else {
        // New project: full data
        const templateFilter = buildTemplateFilterFromCategories();
        const additionalRequests = [];
        if (addOns.elevatedUtilities) additionalRequests.push('3D/Elevated Pipe');
        if (addOns.hasErosionSurface) additionalRequests.push('Initial Erosion Surface');

        // Fetch a fresh job number from the DB at submit time (single source of
        // truth) instead of the board's stale client-side value. Prevents two new
        // projects from being stamped the same number; the DB unique index is the
        // final guard and the retry below handles a lost race.
        createData.job_number = await ProjectService.getNextJobNumber();
        createData.rev = 0;
        createData.client_id = formData.clientId || null;
        createData.contact_id = formData.contactId || null;
        createData.approximate_acreage = formData.approximateAcreage
          ? parseFloat(formData.approximateAcreage)
          : null;
        createData.template_filter = templateFilter;
        createData.additional_requests = additionalRequests;
        createData.project_type_id = deliverableType === 'surface' ? projectTypeId : null;
        createData.is_linework_only = deliverableType === 'linework';
      }

      try {
        await ProjectService.createProject(createData);
      } catch (createErr) {
        // If we lost a race for the job number, the DB unique index rejects the
        // insert. Grab a fresh number once and retry (new projects only; revisions
        // intentionally share their base project's number).
        const isJobNumberConflict =
          createErr?.code === "23505" ||
          /duplicate key|uniq_primary_job_number/i.test(createErr?.message || "");
        if (isJobNumberConflict && !(step === "revision" && selectedBaseProject)) {
          createData.job_number = await ProjectService.getNextJobNumber();
          await ProjectService.createProject(createData);
        } else {
          throw createErr;
        }
      }

      if (onSave) {
        await onSave();
      }

      onClose();
    } catch (err) {
      console.error("Error creating project:", err);
      setError(err.message || "Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Step 1: Choose type (New Project vs Revision)
  if (step === 'choose_type') {
    return (
      <Modal trackAs="NewProjectModal" isOpen={isOpen} onClose={onClose} size="md">
        <div
          style={{
            padding: "var(--space-8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--space-6)",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: "var(--text-2xl)",
              fontWeight: "var(--font-semibold)",
              color: "var(--gray-50)",
              textAlign: "center",
            }}
          >
            What type of project?
          </h1>

          <div
            style={{
              display: "flex",
              gap: "var(--space-4)",
              width: "100%",
            }}
          >
            <button
              type="button"
              onClick={() => setStep('new_project')}
              style={{
                flex: 1,
                padding: "var(--space-6)",
                backgroundColor: "var(--dark-elevated)",
                border: "2px solid var(--gray-600)",
                borderRadius: "var(--radius-lg)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "var(--space-2)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--cta-blue)";
                e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--gray-600)";
                e.currentTarget.style.backgroundColor = "var(--dark-elevated)";
              }}
            >
              <span style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-semibold)", color: "var(--gray-100)" }}>
                New Project
              </span>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-400)" }}>
                Start fresh
              </span>
            </button>

            <button
              type="button"
              onClick={() => setStep('revision')}
              style={{
                flex: 1,
                padding: "var(--space-6)",
                backgroundColor: "var(--dark-elevated)",
                border: "2px solid var(--gray-600)",
                borderRadius: "var(--radius-lg)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "var(--space-2)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--cta-blue)";
                e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--gray-600)";
                e.currentTarget.style.backgroundColor = "var(--dark-elevated)";
              }}
            >
              <GitBranch size={24} style={{ color: "var(--gray-300)" }} />
              <span style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-semibold)", color: "var(--gray-100)" }}>
                Revision
              </span>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-400)" }}>
                Update existing project
              </span>
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Step 2a: Revision flow (simplified)
  if (step === 'revision') {
    return (
      <Modal trackAs="NewProjectModal" isOpen={isOpen} onClose={onClose} size="lg">
        <div
          style={{
            padding: "var(--space-6)",
            borderBottom: "1px solid var(--gray-700)",
            background: "linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))",
            borderTopLeftRadius: "var(--radius-xl)",
            borderTopRightRadius: "var(--radius-xl)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            <button
              type="button"
              onClick={() => setStep('choose_type')}
              style={{
                padding: "var(--space-2)",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "var(--radius-md)",
                cursor: "pointer",
                color: "var(--gray-400)",
              }}
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: "var(--text-xl)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--gray-50)",
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                }}
              >
                <GitBranch size={20} />
                Create Revision
              </h1>
            </div>
          </div>
        </div>

        <ModalBody>
          {error && (
            <div
              style={{
                padding: "var(--space-3)",
                marginBottom: "var(--space-4)",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid var(--danger)",
                borderRadius: "var(--radius-md)",
                color: "var(--danger)",
                fontSize: "var(--text-sm)",
                display: "flex",
                alignItems: "center",
                gap: "var(--space-2)",
              }}
            >
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", minHeight: "300px" }}>
            {/* Base Project Selection */}
            <div>
              <label style={{
                display: "block",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                color: "var(--gray-300)",
                marginBottom: "var(--space-2)",
              }}>
                Select Base Project
              </label>
              <BaseProjectSelect
                clientId={null} // Allow all clients
                value={selectedBaseProjectId}
                onChange={handleBaseProjectSelect}
                placeholder="Search for project..."
              />
            </div>

            {/* Project Name - only show after base project selected */}
            {selectedBaseProject && (
              <div>
                <label style={{
                  display: "block",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  color: "var(--gray-300)",
                  marginBottom: "var(--space-2)",
                }}>
                  Project Name
                </label>
                <Input
                  value={formData.projectName}
                  onChange={(e) => handleInputChange("projectName", e.target.value)}
                  placeholder={`${selectedBaseProject.name} Revision`}
                />
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <div style={{ display: "flex", justifyContent: "flex-end", width: "100%", gap: "var(--space-3)" }}>
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading || !selectedBaseProjectId || !formData.projectName}
            >
              <Save size={16} style={{ marginRight: "var(--space-2)" }} />
              {loading ? "Creating..." : "Create Revision"}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }

  // Step 2b: New Project flow (full form)
  return (
    <Modal trackAs="NewProjectModal" isOpen={isOpen} onClose={onClose} size="lg">
      <div
        style={{
          padding: "var(--space-6)",
          borderBottom: "1px solid var(--gray-700)",
          background: "linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))",
          borderTopLeftRadius: "var(--radius-xl)",
          borderTopRightRadius: "var(--radius-xl)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <button
            type="button"
            onClick={() => setStep('choose_type')}
            style={{
              padding: "var(--space-2)",
              backgroundColor: "transparent",
              border: "none",
              borderRadius: "var(--radius-md)",
              cursor: "pointer",
              color: "var(--gray-400)",
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: "var(--text-xl)",
                fontWeight: "var(--font-semibold)",
                color: "var(--gray-50)",
              }}
            >
              New Project
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: "var(--space-1)",
                fontSize: "var(--text-sm)",
                color: "var(--gray-400)",
              }}
            >
              Project Setup
            </p>
          </div>
        </div>
      </div>

      <ModalBody>
        {error && (
          <div
            style={{
              padding: "var(--space-3)",
              marginBottom: "var(--space-4)",
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              border: "1px solid var(--danger)",
              borderRadius: "var(--radius-md)",
              color: "var(--danger)",
              fontSize: "var(--text-sm)",
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
            }}
          >
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
          {/* Basic Information Section */}
          <div>
            <h2 style={{
              margin: 0,
              marginBottom: "var(--space-4)",
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--gray-200)",
            }}>
              Basic Information
            </h2>

            {/* Project Name */}
            <div style={{ marginBottom: "var(--space-4)" }}>
              <Input
                label="Project Name"
                value={formData.projectName}
                onChange={(e) => handleInputChange("projectName", e.target.value)}
                placeholder="Enter project name"
                required
              />
            </div>
          </div>

          {/* Client and Contact Section */}
          <div>
            <h2 style={{
              margin: 0,
              marginBottom: "var(--space-4)",
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--gray-200)",
            }}>
              Client Information
            </h2>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
              <Dropdown
                label="Client"
                options={clientOptions}
                value={formData.clientId}
                onChange={(value) => handleInputChange("clientId", value)}
                placeholder="Search for client..."
                searchable
              />
              <Dropdown
                label="Project Contact"
                options={contactOptions}
                value={formData.contactId}
                onChange={(value) => handleInputChange("contactId", value)}
                placeholder={formData.clientId ? "Select contact..." : "Select client first..."}
                disabled={!formData.clientId}
              />
            </div>
          </div>

          {/* Project Details Section */}
          <div>
            <h2 style={{
              margin: 0,
              marginBottom: "var(--space-4)",
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--gray-200)",
            }}>
              Project Details
            </h2>

            {/* Deliverable Type and Acreage side by side */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)", marginBottom: "var(--space-4)" }}>
              {/* Deliverable Type */}
              <div>
                <label style={{
                  display: "block",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  color: "var(--gray-300)",
                  marginBottom: "var(--space-2)",
                }}>
                  Deliverable Type
                </label>
                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  <button
                    type="button"
                    onClick={() => setDeliverableType('surface')}
                    style={{
                      flex: 1,
                      padding: "var(--space-2) var(--space-3)",
                      backgroundColor: deliverableType === 'surface' ? "rgba(59, 130, 246, 0.2)" : "var(--dark-elevated)",
                      border: `1px solid ${deliverableType === 'surface' ? "var(--cta-blue)" : "var(--gray-600)"}`,
                      borderRadius: "var(--radius-md)",
                      color: deliverableType === 'surface' ? "var(--gray-50)" : "var(--gray-400)",
                      fontWeight: deliverableType === 'surface' ? "var(--font-semibold)" : "var(--font-normal)",
                      cursor: "pointer",
                      fontSize: "var(--text-sm)",
                    }}
                  >
                    Surface
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeliverableType('linework');
                      setProjectTypeId(null);
                    }}
                    style={{
                      flex: 1,
                      padding: "var(--space-2) var(--space-3)",
                      backgroundColor: deliverableType === 'linework' ? "rgba(59, 130, 246, 0.2)" : "var(--dark-elevated)",
                      border: `1px solid ${deliverableType === 'linework' ? "var(--cta-blue)" : "var(--gray-600)"}`,
                      borderRadius: "var(--radius-md)",
                      color: deliverableType === 'linework' ? "var(--gray-50)" : "var(--gray-400)",
                      fontWeight: deliverableType === 'linework' ? "var(--font-semibold)" : "var(--font-normal)",
                      cursor: "pointer",
                      fontSize: "var(--text-sm)",
                    }}
                  >
                    Linework
                  </button>
                </div>
              </div>

              {/* Approximate Acreage */}
              <div>
                <Input
                  label="Approximate Acreage"
                  type="number"
                  step="0.01"
                  value={formData.approximateAcreage}
                  onChange={(e) => handleInputChange("approximateAcreage", e.target.value)}
                  placeholder="Enter acreage..."
                />
              </div>
            </div>

            {/* Project Type - Only for Surface Design */}
            {deliverableType === 'surface' && (
              <div style={{ marginBottom: "var(--space-4)" }}>
                <Dropdown
                  label="Project Type"
                  options={projectTypeOptions}
                  value={projectTypeId}
                  onChange={(value) => setProjectTypeId(value)}
                  placeholder="Select project type..."
                  searchable
                />
              </div>
            )}

            {/* Add-ons */}
            <div>
              <label style={{
                display: "block",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                color: "var(--gray-300)",
                marginBottom: "var(--space-2)",
              }}>
                Add-ons
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                {/* Has Pond - Hide if project type is Pond */}
                {!(projectTypeId && categories.find(c => c.id === projectTypeId)?.name === 'Pond') && (
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-2)",
                    padding: "var(--space-2)",
                    backgroundColor: addOns.hasPond ? "rgba(59, 130, 246, 0.1)" : "transparent",
                    border: `1px solid ${addOns.hasPond ? "var(--cta-blue)" : "var(--gray-600)"}`,
                    borderRadius: "var(--radius-md)",
                    cursor: "pointer",
                  }}>
                    <input
                      type="checkbox"
                      checked={addOns.hasPond}
                      onChange={() => handleAddOnToggle('hasPond')}
                    />
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-200)" }}>
                      Project Has a Pond
                    </span>
                    <span style={{ fontSize: "var(--text-xs)", color: "var(--warning)", fontStyle: "italic" }}>
                      *verify for each project
                    </span>
                  </label>
                )}

                <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                  padding: "var(--space-2)",
                  backgroundColor: addOns.elevatedUtilities ? "rgba(59, 130, 246, 0.1)" : "transparent",
                  border: `1px solid ${addOns.elevatedUtilities ? "var(--cta-blue)" : "var(--gray-600)"}`,
                  borderRadius: "var(--radius-md)",
                  cursor: "pointer",
                }}>
                  <input
                    type="checkbox"
                    checked={addOns.elevatedUtilities}
                    onChange={() => handleAddOnToggle('elevatedUtilities')}
                  />
                  <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-200)" }}>Elevated Utilities</span>
                </label>

                <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                  padding: "var(--space-2)",
                  backgroundColor: addOns.hasErosionSurface ? "rgba(59, 130, 246, 0.1)" : "transparent",
                  border: `1px solid ${addOns.hasErosionSurface ? "var(--cta-blue)" : "var(--gray-600)"}`,
                  borderRadius: "var(--radius-md)",
                  cursor: "pointer",
                }}>
                  <input
                    type="checkbox"
                    checked={addOns.hasErosionSurface}
                    onChange={() => handleAddOnToggle('hasErosionSurface')}
                  />
                  <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-200)" }}>Has Erosion Surface</span>
                </label>

                <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                  padding: "var(--space-2)",
                  backgroundColor: addOns.advancedUtilityModel ? "rgba(251, 191, 36, 0.1)" : "transparent",
                  border: `1px solid ${addOns.advancedUtilityModel ? "var(--warning)" : "var(--gray-600)"}`,
                  borderRadius: "var(--radius-md)",
                  cursor: "pointer",
                }}>
                  <input
                    type="checkbox"
                    checked={addOns.advancedUtilityModel}
                    onChange={() => handleAddOnToggle('advancedUtilityModel')}
                  />
                  <span style={{ fontSize: "var(--text-sm)", color: addOns.advancedUtilityModel ? "var(--warning)" : "var(--gray-200)" }}>
                    Advanced Utility Model
                  </span>
                </label>

                {/* Has Centerline Profiles - Only show for Subdivision */}
                {projectTypeId && categories.find(c => c.id === projectTypeId)?.name === "Subdivision" && (
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-2)",
                    padding: "var(--space-2)",
                    backgroundColor: addOns.hasCenterlineProfiles ? "rgba(59, 130, 246, 0.1)" : "transparent",
                    border: `1px solid ${addOns.hasCenterlineProfiles ? "var(--cta-blue)" : "var(--gray-600)"}`,
                    borderRadius: "var(--radius-md)",
                    cursor: "pointer",
                  }}>
                    <input
                      type="checkbox"
                      checked={addOns.hasCenterlineProfiles}
                      onChange={() => handleAddOnToggle('hasCenterlineProfiles')}
                    />
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-200)" }}>
                      Has Centerline Profiles
                    </span>
                    <span style={{ fontSize: "var(--text-xs)", color: "var(--warning)", fontStyle: "italic" }}>
                      *verify for each project
                    </span>
                  </label>
                )}

                {addOns.advancedUtilityModel && (
                  <div style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "var(--space-2)",
                    padding: "var(--space-3)",
                    backgroundColor: "rgba(251, 191, 36, 0.1)",
                    border: "1px solid var(--warning)",
                    borderRadius: "var(--radius-md)",
                  }}>
                    <Info size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--warning)" }}>
                      Advanced Utility Model requires additional setup.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <div style={{ display: "flex", justifyContent: "flex-end", width: "100%", gap: "var(--space-3)" }}>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            <Save size={16} style={{ marginRight: "var(--space-2)" }} />
            {loading ? "Creating..." : "Create Project"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default NewProjectModal;
